import React from 'react'
import { ProgressBar, Tooltip } from '@enamelai/design-system'
import type { PatientARAnalytics } from '@services/api/billing/analytics.api'
import { formatCurrency } from '@utils/billing.utils'
import {
  calculateArPercentage,
  type BillingAgingPeriod,
} from '@utils/billingArAnalytics.util'
import './ArSummaryCards.css'

const clampPercent = (value: number): number =>
  Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0))

export type ArBarColors = {
  fill: string
  track: string
}

const PATIENT_AR_COLORS: ArBarColors = {
  fill: 'var(--ds-blue-500)',
  track: 'var(--ds-blue-200)',
}

const INSURANCE_AR_COLORS: ArBarColors = {
  fill: 'var(--ds-orange-400)',
  track: 'var(--ds-orange-200)',
}

function ArProgress({
  value,
  colors,
  label,
}: {
  value: number
  colors: ArBarColors
  label: string
}): React.ReactElement {
  return (
    <ProgressBar
      className="ar-summary-progress"
      value={clampPercent(value)}
      hideLabel
      aria-label={label}
      style={
        {
          '--ar-summary-progress-fill': colors.fill,
          '--ar-summary-progress-track': colors.track,
        } as React.CSSProperties
      }
    />
  )
}

type ArBalanceCardProps = {
  title: string
  total: number
  label: string
  value: number
  progress: number
  colors: ArBarColors
}

export function ArBalanceCard({
  title,
  total,
  label,
  value,
  progress,
  colors,
}: ArBalanceCardProps): React.ReactElement {
  return (
    <article className="ar-summary-card ar-summary-card--balance">
      <div className="ar-summary-card__head">
        <p className="ar-summary-card__label">{title}</p>
        <p className="ar-summary-card__value">{formatCurrency(total)}</p>
      </div>
      <div className="ar-summary-card__foot">
        <div className="ar-summary-card__secondary">
          <span>{label}</span>
          <span>{formatCurrency(value)}</span>
        </div>
        <ArProgress
          value={progress}
          colors={colors}
          label={`${label} progress`}
        />
      </div>
    </article>
  )
}

type ArAgingSummaryCardProps = {
  totalValue: number
  agingPeriods: BillingAgingPeriod[]
  topColors: ArBarColors
  bottomColors: ArBarColors
}

export function ArAgingSummaryCard({
  totalValue,
  agingPeriods,
  topColors,
  bottomColors,
}: ArAgingSummaryCardProps): React.ReactElement {
  return (
    <article className="ar-summary-card">
      <div className="ar-summary-card__head ar-summary-card__head--aging">
        <p className="ar-summary-card__label">AR aging</p>
        <p className="ar-summary-card__value">{formatCurrency(totalValue)}</p>
      </div>
      <div className="ar-summary-aging__periods">
        {agingPeriods.map((period) => (
          <div key={period.label} className="ar-summary-aging__period">
            <Tooltip
              placement="top"
              content={`Patient AR: ${formatCurrency(period.patientAmount)} · Insurance AR: ${formatCurrency(period.insuranceAmount)}`}
            >
              <div className="ar-summary-aging__bars">
                <ArProgress
                  value={period.topBarValue}
                  colors={{
                    ...topColors,
                    fill: period.topBarProgressColor || topColors.fill,
                  }}
                  label={`${period.label} patient AR`}
                />
                <ArProgress
                  value={period.bottomBarValue}
                  colors={{
                    ...bottomColors,
                    fill: period.bottomBarProgressColor || bottomColors.fill,
                  }}
                  label={`${period.label} insurance AR`}
                />
              </div>
            </Tooltip>
            <span className="ar-summary-aging__period-label">
              {period.label}
            </span>
          </div>
        ))}
      </div>
    </article>
  )
}

export type ArSummaryCardsProps = {
  patientAR?: PatientARAnalytics | null
  insuranceAR?: PatientARAnalytics | null
  agingPeriods: BillingAgingPeriod[]
  arAgingTotal: number
}

export function ArSummaryCards({
  patientAR,
  insuranceAR,
  agingPeriods,
  arAgingTotal,
}: ArSummaryCardsProps): React.ReactElement {
  return (
    <section className="ar-summary-cards" aria-label="AR summary">
      <ArBalanceCard
        title="Total patient AR"
        total={patientAR?.total || 0}
        label="Patient balance"
        value={patientAR?.balance || 0}
        progress={calculateArPercentage(
          patientAR?.balance,
          patientAR?.total || 0,
        )}
        colors={PATIENT_AR_COLORS}
      />
      <ArBalanceCard
        title="Total insurance AR"
        total={insuranceAR?.total || 0}
        label="Insurance pending"
        value={insuranceAR?.balance || 0}
        progress={calculateArPercentage(
          insuranceAR?.balance,
          insuranceAR?.total || 0,
        )}
        colors={INSURANCE_AR_COLORS}
      />
      <ArAgingSummaryCard
        topColors={PATIENT_AR_COLORS}
        bottomColors={INSURANCE_AR_COLORS}
        totalValue={arAgingTotal}
        agingPeriods={agingPeriods}
      />
    </section>
  )
}

export default ArSummaryCards
